/**
 * WebSocket hook for CEISA riwayat respon real-time notifications
 *
 * Connects to backend WebSocket to receive notifications when new
 * riwayat respon entries are saved for a specific PPJK NPWP.
 *
 * Usage:
 * ```tsx
 * const { notifications, isConnected, clearNotifications } = useCeisaWebSocket(ppjkNpwp);
 * ```
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { publishPpjkEvent } from '@/features/realtime/ppjkEvents';

export interface RiwayatResponEntry {
  id: number;
  nomor_aju: string;
  nomor_ppjk: string;
  kode_respon: string;
  uraian_singkat_respon: string;
  waktu_respon: string;
  file_url?: string;
  nama_perusahaan?: string;
  [key: string]: any;
}

export interface RiwayatResponNotification {
  aju_no: string;
  ppjk_npwp: string;
  count?: number;
  entries?: RiwayatResponEntry[];
  message?: string;
  timestamp: number;
  type?: string;
  // Flat format fields (from backend)
  kode_respon?: string;
  uraian_respon?: string;
  nomor_respon?: string;
  waktu_respon?: string;
  nama_perusahaan?: string;
  company_name?: string;
  [key: string]: any;
}

/**
 * One popup in the notification stack. Auto-dismiss timing lives in the
 * container (so it can pause while the user hovers), not in the hooks.
 */
export interface CeisaVisualNotification {
  id: string;
  notification: RiwayatResponNotification;
  /** When the backend received/processed the respon (ms epoch). */
  receivedAt: number;
  /** Auto-dismiss delay in ms; null = stays until closed. */
  durationMs: number | null;
}

/** Backend stamps `timestamp` with Unix seconds; accept ms too, fall back to now. */
export const toReceivedAt = (timestamp?: number): number => {
  if (!timestamp || !Number.isFinite(timestamp)) return Date.now();
  return timestamp < 1e12 ? timestamp * 1000 : timestamp;
};

export interface AjuStatusNotification {
  aju_no: string;
  ppjk_npwp: string;
  company_name?: string;
  keterangan?: string;
  kode_dokumen?: string;
  kode_proses?: string;
  nomor_daftar?: string;
  waktu_status?: string;
  message?: string;
  timestamp: number;
  type?: string;
}

export interface WebSocketMessage {
  type: 'connection_established' | 'new_riwayat_respon' | 'new_aju_status' | 'pong' | string;
  processId?: number;
  data?: any;
  timestamp?: number;
}

const getWebSocketUrl = (ppjkNpwp: string): string => {
  const wsBaseUrl = import.meta.env.VITE_WS_BASE_URL as string | undefined;
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL as string | undefined;
  const accessToken = localStorage.getItem('accessToken');

  let baseUrl: string;
  if (window.location.hostname === 'jar-vis.com' || window.location.hostname.endsWith('.jar-vis.com')) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    baseUrl = `${protocol}//${window.location.host}`;
  } else if (wsBaseUrl) {
    // Use configured WebSocket URL
    baseUrl = wsBaseUrl.endsWith('/') ? wsBaseUrl.slice(0, -1) : wsBaseUrl;
  } else if (apiBaseUrl) {
    // Derive WebSocket URL from API base URL
    baseUrl = apiBaseUrl.replace(/^http/, 'ws').replace(/^https/, 'wss');
    if (baseUrl.endsWith('/')) {
      baseUrl = baseUrl.slice(0, -1);
    }
  } else {
    // Fallback: derive from current location
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    baseUrl = `${protocol}//${host}`;
  }

  // Add token as query parameter for authentication
  const url = `${baseUrl}/api/v1/ceisa/riwayat-respon/ws?channel=${ppjkNpwp}`;
  if (accessToken) {
    return `${url}&token=${accessToken}`;
  }
  return url;
};

export interface UseCeisaWebSocketReturn {
  notifications: RiwayatResponNotification[];
  isConnected: boolean;
  clearNotifications: () => void;
  reconnect: () => void;
  visualNotifications: CeisaVisualNotification[];
  removeVisualNotification: (id: string) => void;
}

export const useCeisaWebSocket = (ppjkNpwp?: string): UseCeisaWebSocketReturn => {
  const [notifications, setNotifications] = useState<RiwayatResponNotification[]>([]);
  const [visualNotifications, setVisualNotifications] = useState<CeisaVisualNotification[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const baseReconnectDelay = 5000; // 5 seconds

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const removeVisualNotification = useCallback((id: string) => {
    setVisualNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const cleanup = useCallback(() => {
    // Clear reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Clear ping interval
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    // Close WebSocket connection
    if (wsRef.current) {
      const ws = wsRef.current;
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;

      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close();
      }

      wsRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!ppjkNpwp) {
      console.warn('useCeisaWebSocket: No PPJK NPWP provided');
      return;
    }

    // Don't create a new connection if one already exists
    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)
    ) {
      console.log('WebSocket already connected or connecting');
      return;
    }

    try {
      const wsUrl = getWebSocketUrl(ppjkNpwp);
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);

          switch (message.type) {
            case 'connection_established':
              break;

            case 'ping':
              // Respond to backend ping with pong
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'pong' }));
              }
              break;

            case 'new_riwayat_respon':
            case 'new_riwayat_respon_email': {
              const notification = message.data as RiwayatResponNotification;

              // Add to notifications list
              setNotifications((prev) => [notification, ...prev]);

              // Show visual notification
              const notificationId = `ceisa-notif-${Date.now()}-${Math.random()}`;
              // NPD is urgent and stays until manually closed; everything else auto-dismisses after 20s
              const uraianRespon = notification.uraian_respon || notification.entries?.[0]?.uraian_singkat_respon;
              const isNPD = uraianRespon?.toUpperCase().includes('NPD');

              setVisualNotifications((prev) => [
                ...prev,
                {
                  id: notificationId,
                  notification,
                  receivedAt: toReceivedAt(notification.timestamp),
                  durationMs: isNPD ? null : 20000,
                },
              ]);

              // Dispatch custom event for other components to listen
              window.dispatchEvent(
                new CustomEvent('ceisa-riwayat-respon-update', {
                  detail: notification,
                })
              );
              break;
            }

            case 'new_aju_status': {
              const statusData = message.data as AjuStatusNotification;

              // Map status payload to RiwayatResponNotification for reuse in notification UI
              const statusNotification: RiwayatResponNotification = {
                aju_no: statusData.aju_no,
                ppjk_npwp: statusData.ppjk_npwp,
                company_name: statusData.company_name,
                uraian_respon: statusData.keterangan,
                message: statusData.message,
                timestamp: statusData.timestamp,
                type: 'status_update',
                // Preserve original status fields
                kode_dokumen: statusData.kode_dokumen,
                kode_proses: statusData.kode_proses,
                nomor_daftar: statusData.nomor_daftar,
                waktu_status: statusData.waktu_status,
              };

              // Add to notifications list
              setNotifications((prev) => [statusNotification, ...prev]);

              // Show visual notification
              const statusNotifId = `ceisa-status-${Date.now()}-${Math.random()}`;
              setVisualNotifications((prev) => [
                ...prev,
                {
                  id: statusNotifId,
                  notification: statusNotification,
                  receivedAt: toReceivedAt(statusData.timestamp),
                  durationMs: 5000,
                },
              ]);

              // Dispatch custom event for other components (e.g. doc-pabean table refresh)
              window.dispatchEvent(
                new CustomEvent('ceisa-aju-status-update', {
                  detail: statusNotification,
                })
              );
              break;
            }

            case 'pong':
              break;

            // Contract §8: data changed for this PPJK. Handed to the in-app bus;
            // subscribers re-read through the APIs.
            case 'statement_config_changed':
            case 'consignee_master_changed':
            case 'eksportir_master_changed':
            case 'master_barang_changed':
              publishPpjkEvent(message.type, message.data);
              break;

            default:
              break;
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onerror = () => {
        setIsConnected(false);
      };

      ws.onclose = () => {
        setIsConnected(false);

        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Attempt to reconnect with exponential backoff
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = baseReconnectDelay * 2 ** reconnectAttemptsRef.current;
          reconnectAttemptsRef.current += 1;

          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        }
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      setIsConnected(false);
    }
  }, [ppjkNpwp]);

  const reconnect = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    cleanup();
    connect();
  }, [cleanup, connect]);

  // Connect on mount or when ppjkNpwp changes
  useEffect(() => {
    if (ppjkNpwp) {
      connect();
    }

    // Cleanup on unmount or when ppjkNpwp changes
    return cleanup;
  }, [ppjkNpwp, connect, cleanup]);

  return {
    notifications,
    isConnected,
    clearNotifications,
    reconnect,
    visualNotifications,
    removeVisualNotification,
  };
};

export default useCeisaWebSocket;
